import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../../contexts/AuthContext';
import { Loader } from '@rfacil/shared/ui';

interface AuthRedirectProps {
  to?: string;
  children?: React.ReactNode;
}

/**
 * @component AuthRedirect
 * @description Un componente que redirige a los usuarios autenticados fuera de las páginas públicas como la página de inicio de sesión.
 * Si el usuario está autenticado, lo redirige a una ruta especificada (por defecto, al panel de control).
 * También muestra un spinner de carga mientras comprueba el estado de autenticación.
 * @param {object} props Las propiedades del componente.
 * @param {string} [props.to='/'] La ruta a la que redirigir si el usuario está autenticado.
 * @param {React.ReactNode} [props.children] Los componentes secundarios a renderizar si el usuario no está autenticado.
 * @returns {React.ReactElement | null} El componente renderizado o nulo si se está redirigiendo.
 */
export const AuthRedirect: React.FC<AuthRedirectProps> = ({
  to = '/',
  children,
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate(to, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, to]);

  if (isLoading) {
    return (
      <Loader
        message="Validando tu sesión..."
        subtitle="Por favor espera unos segundos"
      />
    );
  }

  if (isAuthenticated) {
    return null; // Se redirigirá automáticamente
  }

  return <>{children}</>;
};

export default AuthRedirect;
